import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Clock, Users, CheckCircle, Mail, Phone, MapPin } from "lucide-react";
import Navigation from "@/components/layout/Navigation";
import Footer from "@/components/layout/Footer";
import NewsletterSubscription from "@/components/layout/NewsletterSubscription";
import ScrollReveal from "@/components/ScrollReveal";
import { CelebrationDialog } from "@/components/ui/CelebrationDialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
const Contact = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    monthlySpend: '',
    company: '',
    phone: '',
    preferredTime: '',
    challenge: ''
  });
  const [projectedSavings, setProjectedSavings] = useState(0);
  const [availableSpots, setAvailableSpots] = useState(2); // Live capacity
  const [showCelebration, setShowCelebration] = useState(false);
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);

  const calculateSavings = (monthlySpend: string) => {
    const spend = parseInt(monthlySpend) || 0;
    const yearlySavings = spend * 12 * 0.6; // 60% savings
    const fiveYearSavings = yearlySavings * 5;
    return fiveYearSavings;
  };
  const handleStep1Submit = () => {
    const savings = calculateSavings(formData.monthlySpend);
    setProjectedSavings(savings);
    setStep(2);
  };
  const formatCurrency = (amount: number) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(1)} Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(1)} L`;
    }
    return `₹${amount.toLocaleString()}`;
  };
  return <div className="min-h-screen bg-background text-foreground">
      <Navigation />
      <main>
        {/* Hero Section */}
        <section className="section-spacing bg-gradient-to-b from-background to-primary/5">
          <div className="container mx-auto px-4 lg:px-8">
            <ScrollReveal delay={100}>
              <div className="text-center mb-12 lg:mb-16">
                <h1 className="text-4xl sm:text-5xl lg:text-7xl font-display font-bold mb-6 lg:mb-8">
                  Ready to Build Your <span className="bg-gradient-brand bg-clip-text text-transparent">Unfair Advantage?</span>
                </h1>
                <p className="text-xl lg:text-2xl text-muted-foreground max-w-5xl mx-auto leading-relaxed">
                  Stop paying agencies to rent their expertise. Build your own world-class content engine that works exclusively for your business, scales with your growth, and becomes more valuable every month.
                </p>
                <div className="mt-8 lg:mt-12 p-6 lg:p-8 glass-panel max-w-4xl mx-auto">
                  <p className="text-lg lg:text-xl text-foreground font-medium">
                    The founders who win in the next decade won't be the ones with the biggest marketing budgets. They'll be the ones who built the most authentic, efficient, and scalable content systems.
                  </p>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              {/* Urgency Banner */}
              <div className="glass-panel p-4 lg:p-6 mb-8 lg:mb-12 max-w-4xl mx-auto">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 lg:w-6 lg:h-6 text-accent" />
                    <div className="text-center sm:text-left">
                      <div className="text-sm lg:text-base font-semibold text-accent">Limited Capacity</div>
                      <div className="text-xs lg:text-sm text-muted-foreground">
                        We onboard 5 new clients/month for hands-on delivery
                      </div>
                    </div>
                  </div>
                  <div className="text-center sm:text-right">
                    <Badge variant="outline" className={`${availableSpots > 0 ? 'border-accent text-accent' : 'border-destructive text-destructive'}`}>
                      {availableSpots > 0 ? `${availableSpots} spots available` : 'Waitlist only'}
                    </Badge>
                    <div className="text-xs text-muted-foreground mt-1">This month</div>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={300}>
              <div className="grid lg:grid-cols-3 gap-8 lg:gap-12 max-w-7xl mx-auto">
                {/* Main Form */}
                <div className="lg:col-span-2">
                  <Card className="glass-panel p-6 lg:p-8">
                  {step === 1 ?
                  // Step 1: Initial Form
                  <div className="space-y-4 lg:space-y-6">
                      <div className="text-center mb-6 lg:mb-8">
                        <h3 className="text-xl lg:text-2xl font-semibold mb-2">Get Your Savings Projection</h3>
                        <p className="text-sm lg:text-base text-muted-foreground">See your potential 5-year savings in 30 seconds</p>
                      </div>

                      <div className="space-y-3 lg:space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
                          <div>
                            <Label className="text-sm lg:text-base font-medium">First Name*</Label>
                            <Input placeholder="John" value={formData.firstName} onChange={e => setFormData({
                            ...formData,
                            firstName: e.target.value
                          })} className="mt-2 min-h-[44px] lg:min-h-[48px]" />
                          </div>
                          <div>
                            <Label className="text-sm lg:text-base font-medium">Last Name*</Label>
                            <Input placeholder="Doe" value={formData.lastName} onChange={e => setFormData({
                            ...formData,
                            lastName: e.target.value
                          })} className="mt-2 min-h-[44px] lg:min-h-[48px]" />
                          </div>
                        </div>

                        <div>
                          <Label className="text-sm lg:text-base font-medium">Work Email*</Label>
                          <Input type="email" placeholder="founder@company.com" value={formData.email} onChange={e => setFormData({
                          ...formData,
                          email: e.target.value
                        })} className="mt-2 min-h-[44px] lg:min-h-[48px]" />
                        </div>

                        <div>
                          <Label className="text-sm lg:text-base font-medium">Your Role*</Label>
                          <Select value={formData.role} onValueChange={value => setFormData({
                          ...formData,
                          role: value
                        })}>
                            <SelectTrigger className="mt-2 min-h-[44px] lg:min-h-[48px]">
                              <SelectValue placeholder="Select your role" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="founder">Founder/CEO</SelectItem>
                              <SelectItem value="cofounder">Co-Founder</SelectItem>
                              <SelectItem value="cmo">CMO/Head of Marketing</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label className="text-sm lg:text-base font-medium">Current Monthly Agency Spend*</Label>
                          <Select value={formData.monthlySpend} onValueChange={value => setFormData({
                          ...formData,
                          monthlySpend: value
                        })}>
                            <SelectTrigger className="mt-2 min-h-[44px] lg:min-h-[48px]">
                              <SelectValue placeholder="Select monthly spend" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="100000">₹1-2 L/month</SelectItem>
                              <SelectItem value="250000">₹2-3 L/month</SelectItem>
                              <SelectItem value="400000">₹3-5 L/month</SelectItem>
                              <SelectItem value="700000">₹5-8 L/month</SelectItem>
                              <SelectItem value="1000000">₹8+ L/month</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <Button variant="hero" size="lg" className="w-full text-sm lg:text-base min-h-[48px] lg:min-h-[56px]" onClick={handleStep1Submit} disabled={!formData.firstName || !formData.lastName || !formData.email || !formData.role || !formData.monthlySpend}>
                        Show My Savings Projection
                      </Button>

                      <p className="text-xs text-muted-foreground text-center">We value your privacy, every interaction with Contencraft is 100% Confidential.</p>
                    </div> :
                  // Step 2: Savings + Extended Form
                  <div className="space-y-4 lg:space-y-6">
                      {/* Savings Display */}
                      <div className="text-center p-4 lg:p-6 glass-panel border-accent/20 bg-accent/5 rounded-lg">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <CheckCircle className="w-5 h-5 lg:w-6 lg:h-6 text-accent" />
                          <span className="text-sm lg:text-base text-accent font-medium">Your Projected 5-Year Savings</span>
                        </div>
                        <div className="text-3xl lg:text-4xl font-bold bg-gradient-brand bg-clip-text text-transparent mb-2">
                          {formatCurrency(projectedSavings)}
                        </div>
                        <p className="text-xs lg:text-sm text-muted-foreground">
                          vs continuing with agency retainers
                        </p>
                      </div>

                      {/* Resource Download */}
                      <div className="p-3 lg:p-4 glass-panel rounded-lg">
                        <div className="flex items-center gap-3 mb-2">
                          <Users className="w-4 h-4 lg:w-5 lg:h-5 text-primary" />
                          <span className="text-sm lg:text-base font-medium text-primary">Bonus: 90-Minute Founder Week Template</span>
                        </div>
                        <p className="text-xs lg:text-sm text-muted-foreground">
                          Instant download: Reduce your content oversight to 90 minutes/week
                        </p>
                      </div>

                      <div className="text-center mb-4 lg:mb-6">
                        <h3 className="text-lg lg:text-xl font-semibold mb-2">Complete Your Application</h3>
                        <p className="text-sm lg:text-base text-muted-foreground">Book your strategy call and get instant access</p>
                      </div>

                      <div className="space-y-3 lg:space-y-4">
                        <div>
                          <Label className="text-sm lg:text-base font-medium">Company Name*</Label>
                          <Input placeholder="Your company name" value={formData.company} onChange={e => setFormData({
                          ...formData,
                          company: e.target.value
                        })} className="mt-2 min-h-[44px] lg:min-h-[48px]" />
                        </div>

                        <div>
                          <Label className="text-sm lg:text-base font-medium">Phone Number*</Label>
                          <Input type="tel" placeholder="+91 98765 43210" value={formData.phone} onChange={e => setFormData({
                          ...formData,
                          phone: e.target.value
                        })} className="mt-2 min-h-[44px] lg:min-h-[48px]" />
                        </div>

                        <div>
                          <Label className="text-sm lg:text-base font-medium">Preferred Call Time*</Label>
                          <Select value={formData.preferredTime} onValueChange={value => setFormData({
                          ...formData,
                          preferredTime: value
                        })}>
                            <SelectTrigger className="mt-2 min-h-[44px] lg:min-h-[48px]">
                              <SelectValue placeholder="When works best?" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="morning">Morning (9 AM - 12 PM)</SelectItem>
                              <SelectItem value="afternoon">Afternoon (12 PM - 4 PM)</SelectItem>
                              <SelectItem value="evening">Evening (4 PM - 7 PM)</SelectItem>
                              <SelectItem value="flexible">Flexible timing</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label className="text-sm lg:text-base font-medium">Biggest Content Challenge</Label>
                          <Select value={formData.challenge} onValueChange={value => setFormData({
                          ...formData,
                          challenge: value
                        })}>
                            <SelectTrigger className="mt-2 min-h-[44px] lg:min-h-[48px]">
                              <SelectValue placeholder="What's your main pain point?" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="consistency">Inconsistent content output</SelectItem>
                              <SelectItem value="quality">Quality not meeting standards</SelectItem>
                              <SelectItem value="cost">Agency costs too high</SelectItem>
                              <SelectItem value="control">Lack of control/ownership</SelectItem>
                              <SelectItem value="scaling">Can't scale content efforts</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <Button 
                        variant="hero" 
                        size="lg" 
                        className="w-full text-sm lg:text-base min-h-[48px] lg:min-h-[56px]" 
                        disabled={!formData.company || !formData.phone || !formData.preferredTime || isSubmittingContact}
                         onClick={async () => {
                          setIsSubmittingContact(true);
                          const loadingId = toast.loading("Submitting...");
                          
                          try {
                            const { data, error } = await supabase.functions.invoke('submit-contact', {
                              body: {
                                name: `${formData.firstName} ${formData.lastName}`,
                                email: formData.email,
                                role: formData.role,
                                monthlySpend: formData.monthlySpend,
                                companyName: formData.company,
                                phone: formData.phone,
                                preferredCallTime: formData.preferredTime,
                                contentChallenge: formData.challenge,
                                projectedSavings: projectedSavings
                              }
                            });

                            if (error) {
                              console.error('Contact submission error:', error);
                              if (error.message?.includes('429')) {
                                toast.error("Too many requests", {
                                  description: "Please try again in a few minutes."
                                });
                              } else {
                                toast.error("Submission failed", {
                                  description: "Please try again or contact support."
                                });
                              }
                              return;
                            }

                            if (data?.success) {
                              toast.success("Your spot is secured!", {
                                description: "We'll contact you within 24 hours to schedule your strategy call."
                              });
                              setShowCelebration(true);
                            }
                          } catch (err) {
                            console.error('Contact submission error:', err);
                            toast.error("Submission failed", {
                              description: "Please try again or contact support."
                            });
                          } finally {
                            setIsSubmittingContact(false);
                            toast.dismiss(loadingId);
                          }
                        }}
                      >
                        {isSubmittingContact ? 'Submitting...' : (availableSpots > 0 ? 'Secure My Spot' : 'Join Waitlist')}
                      </Button>

                      {availableSpots > 0 && <p className="text-xs text-center text-accent">
                          🔥 Only {availableSpots} spots left this month
                        </p>}

                      <p className="text-xs text-muted-foreground text-center">
                        Meeting confirmation + resource download will be sent immediately
                      </p>
                    </div>}
                  </Card>
                </div>

                {/* Side Boxes */}
                <div className="space-y-6">
                  {/* Get in Touch Box */}
                  <Card className="glass-panel p-6">
                    <h3 className="text-xl font-semibold mb-6">Get in Touch</h3>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <Mail className="w-5 h-5 text-primary mt-0.5" />
                        <div>
                          <div className="text-sm font-medium text-muted-foreground">Email</div>
                          <div className="text-sm">hello@contencraft.com</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Phone className="w-5 h-5 text-primary mt-0.5" />
                        <div>
                          <div className="text-sm font-medium text-muted-foreground">Phone</div>
                          <div className="text-sm">+91 7726020403</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-primary mt-0.5" />
                        <div>
                          <div className="text-sm font-medium text-muted-foreground">Location</div>
                          <div className="text-sm">India (IST)</div>
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Ready to Get Started Box */}
                  <Card className="glass-panel p-6">
                    <h3 className="text-xl font-semibold mb-4">Ready to Get Started?</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Join 47+ companies that have built their content capability with Contencraft. Book a call to discuss your specific needs and timeline.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-accent" />
                        <span>Free consultation</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-accent" />
                        <span>Custom proposal</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-accent" />
                        <span>Clear timeline</span>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>
      </main>
      <NewsletterSubscription />
      <Footer />
      
      <CelebrationDialog
        open={showCelebration}
        onClose={() => setShowCelebration(false)}
        type="contact"
        projectedSavings={formatCurrency(projectedSavings)}
      />
    </div>;
};
export default Contact;